

import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  Text,
  Input,
  IconButton,
  VStack,
  HStack,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton
} from "@chakra-ui/react";
import * as XLSX from "xlsx";

const SheetDownload = ({ isOpen, onClose, products, platforms }) => {
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [columns, setColumns] = useState([]); // Stores { name: "ColumnName", key: "productKey" }
  
  // Manual add inputs
  const [newColumnName, setNewColumnName] = useState("");
  const [newColumnKey, setNewColumnKey] = useState("");
  
  const [abuseType, setAbuseType] = useState("");
  const userInfo = JSON.parse(localStorage.getItem("PAuser"));
  const userProductSheet = JSON.parse(userInfo?.message?.result?.product_sheet) || " "
  // Filter products where seller_status is "R"
  let filteredProducts = products
    .filter((product) => product.seller_status === "R")
    .map((it) => it.product);

  // Get product keys dynamically
  const productKeys = filteredProducts.length
    ? Object.keys(filteredProducts[0])
    : [];

  const handlePlatformChange = (e) => {
    const platformName = e.target.value;
    setSelectedPlatform(platformName);

    const selectedPlatObj = platforms.find(
      (p) => p.platform_name === platformName
    );

    if (selectedPlatObj && selectedPlatObj.product_sheet) {
      try {
        // The data comes as a string: '{"URL": "prod_link", "Brand": "keyword"}'
        const temp = userProductSheet?.[platformName]
        // console.log(temp)
      const sheetTemplate = temp ? temp : JSON.parse(selectedPlatObj.product_sheet);

        // Convert object to array format for our state: [{name: "URL", key: "prod_link"}, ...]
        const templateColumns = Object.entries(sheetTemplate).map(
          ([colName, prodKey]) => ({
            name: colName,
            key: prodKey,
          })
        );

        setColumns(templateColumns);
      } catch (error) {
        console.error("Error parsing product_sheet JSON:", error);
        setColumns([]);
      }


  } else {
    setColumns([]);  // Reset columns if no valid product sheet found
  }
};


  // Add a new manual column
  const handleAddColumn = () => {
    if (newColumnName && newColumnKey) {
      setColumns([...columns, { name: newColumnName, key: newColumnKey }]);
      setNewColumnName("");
      setNewColumnKey("");
    }
  };

  // Remove a column
  const handleRemoveColumn = (indexToRemove) => {
    setColumns(columns.filter((_, index) => index !== indexToRemove));
  };

  const handleReset = () => {
    setSelectedPlatform("");
    setColumns([]);
    setNewColumnName("");
    setNewColumnKey("");
    setAbuseType("");
  };

  // Export data to Excel
  const handleDownload = () => {
    const currentDate = new Date()
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(",", ""); // Format: 12-Dec-2024

    const data = filteredProducts.map((product, index) => {
      let row = {
        "Sr. No": index + 1,
      };
      columns.forEach((col) => {
        // Map the Column Name (col.name) to the Product Value (product[col.key])
        row[col.name] = product[col.key] || ""; 
      });
      
      // Only add Abuse Type if user typed something
      if(abuseType) {
        row["Abuse Type"] = abuseType;
      }
      
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    
    // Safely get user name
    const paUser = JSON.parse(localStorage.getItem("PAuser"));
    let user_name = paUser?.message?.result?.name || "User";

    if (user_name) {
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const fileName = selectedPlatform 
        ? `${user_name}_${selectedPlatform}_${currentDate}.xlsx` 
        : `${user_name}_CustomSheet_${currentDate}.xlsx`;
        
      XLSX.writeFile(workbook, fileName);
    } else {
      alert("Please Login first");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Product Sheet</ModalHeader>
        <ModalBody>
          <Box mb={5}>
            <Text mb={2} fontWeight="bold">Select Platform:</Text>
            <Select
              placeholder="Select Platform"
              value={selectedPlatform}
              onChange={handlePlatformChange}
            >
              {platforms.map((platform) => (
                <option key={platform.id} value={platform.platform_name}>
                  {platform.platform_name}
                </option>
              ))}
            </Select>
          </Box>

          <Divider mb={4} />

          <Box mb={4}>
             <Text mb={2} fontWeight="bold">Columns to Export:</Text>
             {columns.length === 0 ? (
                <Text fontSize="sm" color="gray.500">No columns selected. Select a platform or add manually.</Text>
             ) : (
                <Flex wrap="wrap" gap={2}>
                    {columns.map((col, index) => (
                        <Tag key={index} size="md" borderRadius="full" variant="solid" colorScheme="teal">
                        <TagLabel>{col.name} ({col.key})</TagLabel>
                        <TagCloseButton onClick={() => handleRemoveColumn(index)} />
                        </Tag>
                    ))}
                </Flex>
             )}
          </Box>

          <Box mb={5} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
            <Text mb={2} fontSize="sm" fontWeight="bold">Add Extra Column:</Text>
            <Flex gap={2}>
              <Input
                placeholder="Column Name"
                size="sm"
                bg="white"
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
              />
              <Select
                placeholder="Product Key"
                size="sm"
                bg="white"
                value={newColumnKey}
                onChange={(e) => setNewColumnKey(e.target.value)}
              >
                {productKeys.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </Select>
              <Button onClick={handleAddColumn} size="sm" colorScheme="blue">
                +
              </Button>
            </Flex>
          </Box>

          <Box mb={4}>
            <Text mb={2} fontWeight="bold">Abuse Type :</Text>
            <Input
              placeholder="Enter Abuse Type"
              value={abuseType}
              onChange={(e) => setAbuseType(e.target.value)}
            />
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="green" mr={3} onClick={handleDownload} isDisabled={columns.length === 0}>
            Download Excel
          </Button>
          <Button
            onClick={() => {
              handleReset();
              onClose();
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SheetDownload;
